import { useQuery, useQueryClient, useQueries } from "@tanstack/vue-query";
import { computed, unref, type MaybeRef, watchEffect } from "vue";
import { api } from "./api";
import { createApiQueryByIdFn, TeamsApi, type TeamResponse } from "@xs2event/js-lib/api";

const teamsApi = new TeamsApi();

export const useTeams = (payload?: MaybeRef) => {
  return useQuery({
    queryKey: computed(() => ["teams", payload && unref(payload)]),
    queryFn: async () => {
      const response = await api.get("/teams", {
        params: { ...unref(payload) },
      });

      return response.data;
    },
    staleTime: 1000 * 60 * 30, //half an hour,
  });
};

// Batch fetch teams by IDs
export const useGetTeamsByIds = createApiQueryByIdFn(teamsApi, "teamsGet", "teamId");

// Prefetch teams for visible events and cache them individually
export const usePrefetchTeamsForEvents = (events: MaybeRef<any[]>, options?: { enabled?: MaybeRef<boolean> }) => {
  const queryClient = useQueryClient();
  
  // Extract unique team IDs
  const teamIds = computed(() => {
    const evs = unref(events) || [];
    const ids = new Set<string>();
    evs.forEach((event: any) => {
      if (event.hometeam_id || event.hometeamId) ids.add(event.hometeam_id || event.hometeamId);
      if (event.visiting_id || event.visitingId) ids.add(event.visiting_id || event.visitingId);
    });
    return Array.from(ids).filter(Boolean);
  });
  
  // Fetch all teams in one batched query
  const teamsQuery = useGetTeamsByIds(teamIds, undefined, {
    ...options,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
  
  // Cache individual teams when data arrives - use watch instead of watchEffect for better reactivity
  watchEffect(() => {
    const teams = teamsQuery.data.value;
    if (teams?.length) {
      teams.forEach((team: any) => {
        if (team?.teamId) {
          // Cache with the exact same key format that useTeam uses
          queryClient.setQueryData(['teamGet', { teamId: team.teamId }], team);
        }
      });
    }
  });
  
  return teamsQuery;
};

// Helper to get team from cache - uses useQuery to make it reactive to cache updates.
// When prefetchedTeams is provided (e.g. from event context), the query is disabled until the
// prefetch settles, so we don't trigger duplicate individual fetches.
export const useTeamFromCache = (
  teamId: MaybeRef<string | undefined>,
  options?: {
    prefetchedTeams?: MaybeRef<any[] | undefined>;
    prefetchIsLoading?: MaybeRef<boolean>;
  }
) => {
  const id = computed(() => unref(teamId));
  const prefetched = computed(() => unref(options?.prefetchedTeams));
  const prefetchLoading = computed(() => !!unref(options?.prefetchIsLoading));
  const teamFromPrefetch = computed(() => {
    const pid = id.value;
    const list = prefetched.value;
    if (!pid || !list?.length) return undefined;
    return list.find((t: any) => t?.teamId === pid);
  });

  // Don't run individual fetch when prefetch is in progress, or when team is already in prefetched list.
  const shouldFetch = computed(() => {
    if (!id.value) return false;
    if (!options?.prefetchedTeams) return true;
    if (prefetchLoading.value) return false;
    return teamFromPrefetch.value === undefined;
  });

  const teamQuery = useQuery({
    queryKey: computed(() => ['teamGet', { teamId: id.value }]),
    queryFn: async () => {
      const teamIdValue = id.value;
      if (!teamIdValue) return null;
      return await teamsApi.teamGet({ teamId: teamIdValue });
    },
    enabled: shouldFetch,
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 30,
  });

  return computed(() => teamFromPrefetch.value ?? teamQuery.data.value);
};
